import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";

const M = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μάι", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];
const sel = "rounded-lg border border-[#E3DED4] bg-white px-2 py-1.5 text-xs outline-none focus:border-[#45423C]";

// Monthly counted expenses per P&L line, current year vs previous year.
export const ExpenseMonthlyChart = ({ year }) => {
  const [code, setCode] = useState("all");
  const { data } = useQuery({ queryKey: ["exp-monthly", year], queryFn: async () => (await api.get("/expenses/monthly", { params: { year } })).data });
  const rows = data?.rows || [];
  const pick = code === "all" ? rows.filter((r) => !r.below) : rows.filter((r) => r.code === code);
  const series = M.map((m, i) => {
    const k = String(i + 1);
    return { m, cur: Math.round(pick.reduce((s, r) => s + (r.cur[k] || 0), 0)), prev: Math.round(pick.reduce((s, r) => s + (r.prev[k] || 0), 0)) };
  });
  const tc = pick.reduce((s, r) => s + r.cur_total, 0), tp = pick.reduce((s, r) => s + r.prev_total, 0);
  const pct = tp ? ((tc - tp) / tp) * 100 : null;
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 space-y-3" data-testid="exp-monthly-chart">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm font-medium text-[#45423C]">Μηνιαίες δαπάνες {year} vs {year - 1} <span className="text-[#6F6A60] font-normal">(μόνο όσες μετράνε)</span></div>
        <select data-testid="exp-chart-line" className={sel} value={code} onChange={(e) => setCode(e.target.value)}>
          <option value="all">Όλα τα λειτουργικά (χωρίς κάτω από GOP)</option>
          {rows.map((r) => <option key={r.code} value={r.code}>{r.code} {r.label}{r.below ? " ↓" : ""}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <span data-testid="exp-chart-cur">{year}: <b className="tabular">{fmtEuro(tc)}</b></span>
        <span data-testid="exp-chart-prev" className="text-[#6F6A60]">{year - 1}: <b className="tabular">{fmtEuro(tp)}</b></span>
        {pct !== null && <span data-testid="exp-chart-pct" className={`font-semibold ${pct > 0 ? "text-[#B4412A]" : "text-[#4A5A3E]"}`}>{pct > 0 ? "+" : ""}{pct.toFixed(1)}%</span>}
        {pct === null && tp === 0 && <span className="text-[#8A8577]">δεν υπάρχουν δεδομένα {year - 1} — ανεβάστε το αρχείο της προηγούμενης χρονιάς</span>}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEE9DF" />
            <XAxis dataKey="m" tick={{ fontSize: 11, fill: "#6F6A60" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6F6A60" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={40} />
            <Tooltip formatter={(v, n) => [fmtEuro(v), n]} contentStyle={{ borderRadius: 12, border: "1px solid #E3DED4", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="prev" name={String(year - 1)} fill="#D9D3C7" radius={[4, 4, 0, 0]} />
            <Bar dataKey="cur" name={String(year)} fill="#C86235" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
